import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import AuthService from '../../services/AuthService';
import AuthLayout from './auth/AuthLayout';
import SocialAuth from '../components/Auth/SocialAuth';
import { Button, Field, Input, inputClass } from '../ui';

const STEPS = { FORM: 1, OTP: 2 };

const Stepper = ({ step }) => {
  const items = ['Details', 'Verify', 'Plan'];
  return (
    <div className="tw-mb-6 tw-flex tw-items-center">
      {items.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const active = step === n;
        return (
          <React.Fragment key={label}>
            <div className="tw-flex tw-items-center tw-gap-2">
              <span
                className={`tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-bold ${
                  active || done ? 'tw-bg-brand tw-text-white' : 'tw-bg-sunken tw-text-content-faint'
                }`}
              >
                {done ? <i className="fa-solid fa-check" /> : n}
              </span>
              <span className={`tw-text-xs tw-font-semibold ${active || done ? 'tw-text-heading' : 'tw-text-content-faint'}`}>
                {label}
              </span>
            </div>
            {i < items.length - 1 && (
              <span className={`tw-mx-2 tw-h-px tw-flex-1 ${step > n ? 'tw-bg-brand' : 'tw-bg-line'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const OtpInput = ({ label, icon, digits, setDigits }) => {
  const refs = useRef([]);
  const onChange = (i, v) => {
    if (!/^\d*$/.test(v)) return;
    const d = [...digits];
    d[i] = v.slice(-1);
    setDigits(d);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };
  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };
  const onPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const d = [...digits];
    for (let i = 0; i < 6; i++) d[i] = pasted[i] || '';
    setDigits(d);
    refs.current[Math.min(pasted.length, 5)]?.focus();
  };
  return (
    <div className="tw-mb-4">
      {label && (
        <div className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-content">
          <i className={`fa-solid ${icon} tw-mr-1.5 tw-text-brand`} />{label}
        </div>
      )}
      <div className="tw-flex tw-justify-center tw-gap-2" onPaste={onPaste}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            className={`${inputClass} tw-h-14 tw-w-12 tw-text-center tw-text-xl tw-font-bold`}
            value={digit}
            onChange={(e) => onChange(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
          />
        ))}
      </div>
    </div>
  );
};

const EMPTY = () => ['', '', '', '', '', ''];

function Register() {
  const nav = useNavigate();
  const [step, setStep] = useState(STEPS.FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  // { sms, email } — which channels the admin requires. Null while loading.
  const [vcfg, setVcfg] = useState({ sms: true, email: true });
  const [channels, setChannels] = useState([]); // channels actually used for this signup
  const [verifyKey, setVerifyKey] = useState('');

  const [formData, setFormData] = useState({
    fullName: '', username: '', email: '', password: '',
    confirmPassword: '', phone: '', dateOfBirth: '', joinPlatform: false,
  });
  const [errors, setErrors] = useState({});

  const [smsDigits, setSmsDigits] = useState(EMPTY);
  const [emailDigits, setEmailDigits] = useState(EMPTY);

  useEffect(() => {
    AuthService.getVerificationConfig()
      .then((r) => { if (r.data?.data) setVcfg(r.data.data); })
      .catch(() => { /* keep defaults */ });
    return () => clearInterval(countdownRef.current);
  }, []);

  const startCountdown = () => {
    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const e = {};
    if (!formData.fullName.trim()) e.fullName = 'Full name is required';
    if (formData.username.trim() && !/^[a-zA-Z0-9_]{3,20}$/.test(formData.username.trim()))
      e.username = 'Username must be 3-20 characters (letters, numbers, underscores)';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Enter a valid email address';
    if (!formData.password) e.password = 'Password is required';
    else if (formData.password.length < 6) e.password = 'Password must be at least 6 characters';
    if (!formData.confirmPassword) e.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (vcfg.sms && !formData.phone.trim()) e.phone = 'Phone number is required';
    if (!formData.joinPlatform) e.joinPlatform = 'You must accept the terms to continue';
    return e;
  };

  const finish = useCallback((data) => {
    const { token, user } = data;
    AuthService.saveTokenInLocalStorage({ token, user });
    swal({
      title: 'Welcome to Teve 🎉',
      text: 'Your account is ready. Choose a plan to get started.',
      icon: 'success',
      button: 'Choose a Plan',
    }).then(() => nav('/pricing'));
  }, [nav]);

  const showDevCodes = (d) => {
    const parts = [];
    if (d?.devSmsOtp) parts.push(`SMS: ${d.devSmsOtp}`);
    if (d?.devEmailOtp) parts.push(`Email: ${d.devEmailOtp}`);
    if (parts.length) swal('Dev Mode codes', parts.join('\n'), 'info');
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const v = validateForm();
    if (Object.keys(v).length) { setErrors(v); return; }
    setIsLoading(true);
    try {
      const response = await AuthService.sendOTP({
        fullName: formData.fullName,
        username: formData.username.trim() || undefined,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined,
        dateOfBirth: formData.dateOfBirth,
        joinPlatform: formData.joinPlatform,
      });
      const d = response.data;
      if (!d?.success) return;
      // Both channels disabled -> account created immediately.
      if (d.data?.token) { finish(d.data); return; }
      setVerifyKey(d.verifyKey || formData.phone);
      setChannels(d.channels || ['sms']);
      setSmsDigits(EMPTY());
      setEmailDigits(EMPTY());
      setStep(STEPS.OTP);
      startCountdown();
      showDevCodes(d);
    } catch (error) {
      swal('Error', error.response?.data?.error || 'Failed to send the verification code. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const bothChannels = channels.includes('sms') && channels.includes('email');
  const codeReady = channels.length
    ? (channels.includes('sms') ? smsDigits.join('').length === 6 : true)
      && (channels.includes('email') ? emailDigits.join('').length === 6 : true)
    : false;

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!codeReady) { swal('Error', 'Please enter the complete 6-digit code(s)', 'error'); return; }
    setIsLoading(true);
    try {
      const payload = { verifyKey };
      if (bothChannels) {
        payload.smsOtp = smsDigits.join('');
        payload.emailOtp = emailDigits.join('');
      } else {
        // single channel — server accepts it as `otp`
        payload.otp = (channels.includes('email') ? emailDigits : smsDigits).join('');
      }
      const response = await AuthService.verifyOTPAndRegister(payload);
      if (response.data?.success) finish(response.data.data);
    } catch (error) {
      swal('Error', error.response?.data?.error || 'Verification failed. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setIsLoading(true);
    try {
      const response = await AuthService.resendOTP({ verifyKey });
      if (response.data?.success) {
        startCountdown();
        setSmsDigits(EMPTY());
        setEmailDigits(EMPTY());
        swal('Sent!', response.data.message || 'A new code has been sent.', 'success');
        showDevCodes(response.data);
      }
    } catch {
      swal('Error', 'Failed to resend code. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const dobMax = new Date(new Date().setFullYear(new Date().getFullYear() - 13)).toISOString().split('T')[0];

  const noVerification = !vcfg.sms && !vcfg.email;
  const channelLabel = channels.includes('sms') && channels.includes('email')
    ? 'phone and email'
    : channels.includes('email') ? 'email' : 'phone';

  return (
    <AuthLayout
      eyebrow={step === STEPS.FORM ? 'Get started' : 'Almost there'}
      title={step === STEPS.FORM ? 'Create your account' : 'Verify your account'}
      subtitle={
        step === STEPS.FORM
          ? 'Set up your broadcasting workspace in a minute.'
          : <>We sent a 6-digit code to your {channelLabel}.</>
      }
      footer={
        step === STEPS.FORM ? (
          <>Already have an account? <Link to="/login" className="tw-font-semibold tw-text-brand">Sign in</Link></>
        ) : null
      }
    >
      <Stepper step={step} />

      {step === STEPS.FORM && (
        <form onSubmit={handleSendOTP} className="tw-space-y-4">
          <Field label="Full name" required error={errors.fullName}>
            <Input name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Jane Doe" />
          </Field>
          <Field label="Username" hint="Optional — 3-20 letters, numbers, underscores" error={errors.username}>
            <Input name="username" value={formData.username} onChange={handleChange} placeholder="auto-generated if empty" />
          </Field>
          <Field label="Email address" required error={errors.email}>
            <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" />
          </Field>
          <div className="tw-space-y-4">
            <Field label="Password" required error={errors.password}>
              <Input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Min 6 characters" />
            </Field>
            <Field label="Confirm password" required error={errors.confirmPassword}>
              <Input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="Repeat password" />
            </Field>
          </div>
          <div className="tw-space-y-4">
            {vcfg.sms && (
              <Field label="Phone number" required hint="Include country code (e.g. +1)" error={errors.phone}>
                <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 234 567 8900" />
              </Field>
            )}
            <Field label="Date of birth">
              <Input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} max={dobMax} />
            </Field>
          </div>

          <label className={`tw-flex tw-gap-3 tw-rounded-xl tw-border tw-p-3.5 tw-text-sm ${errors.joinPlatform ? 'tw-border-live/50 tw-bg-live/5' : 'tw-border-line tw-bg-raised'}`}>
            <input type="checkbox" name="joinPlatform" checked={formData.joinPlatform} onChange={handleChange} className="tw-mt-0.5" />
            <span className="tw-text-content-muted">
              I agree to join Teve and accept the{' '}
              <Link to="/terms" target="_blank" className="tw-font-semibold tw-text-brand">Terms of Service</Link> and{' '}
              <Link to="/privacy" target="_blank" className="tw-font-semibold tw-text-brand">Privacy Policy</Link>.
            </span>
          </label>
          {errors.joinPlatform && <div className="tw--mt-2 tw-text-xs tw-text-live">{errors.joinPlatform}</div>}

          <Button type="submit" size="lg" className="tw-w-full" disabled={isLoading}>
            {isLoading
              ? <><i className="fa-solid fa-spinner fa-spin" /> {noVerification ? 'Creating account…' : 'Sending code…'}</>
              : noVerification
                ? <>Create account <i className="fa-solid fa-arrow-right" /></>
                : <>Send verification code <i className="fa-solid fa-arrow-right" /></>}
          </Button>

          <SocialAuth
            onResult={(r) => {
              if (r.success) nav(r.hasSubscription === false ? '/pricing' : '/stream-dashboard');
            }}
          />
        </form>
      )}

      {step === STEPS.OTP && (
        <form onSubmit={handleVerifyOTP}>
          {channels.includes('sms') && (
            <OtpInput
              label={bothChannels ? `Code sent by SMS to ${formData.phone}` : null}
              icon="fa-comment-sms"
              digits={smsDigits}
              setDigits={setSmsDigits}
            />
          )}
          {channels.includes('email') && (
            <OtpInput
              label={bothChannels ? `Code sent by email to ${formData.email}` : null}
              icon="fa-envelope"
              digits={emailDigits}
              setDigits={setEmailDigits}
            />
          )}

          <Button type="submit" size="lg" className="tw-w-full" disabled={isLoading || !codeReady}>
            {isLoading ? <><i className="fa-solid fa-spinner fa-spin" /> Verifying…</> : 'Verify & create account'}
          </Button>

          <div className="tw-mt-4 tw-text-center tw-text-sm tw-text-content-muted">
            Didn't get the code?{' '}
            {countdown > 0 ? (
              <span>Resend in {countdown}s</span>
            ) : (
              <button type="button" className="tw-font-semibold tw-text-brand" onClick={handleResendOTP} disabled={isLoading}>
                Resend code
              </button>
            )}
          </div>
          <div className="tw-mt-2 tw-text-center">
            <button
              type="button"
              className="tw-text-sm tw-text-content-faint hover:tw-text-content"
              onClick={() => { setStep(STEPS.FORM); setSmsDigits(EMPTY()); setEmailDigits(EMPTY()); }}
            >
              <i className="fa-solid fa-arrow-left tw-mr-1" /> Back to details
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}

export default Register;
